/* eslint-disable @typescript-eslint/naming-convention */
import type { IEditorConfig } from '@/version-types';
import type { IService } from '../shared/types/services';
import { AgentLayoutService } from './agent-layout';
import { BehaviorService } from './behavior';
import { BlurService } from './blur';
import { EdgelessCanvasService } from './canvas';
import { CustomDataService } from './custom-data';
import { EditorService } from './editor';
import { EventEmitter, EventGenerator } from './events';
import { FlatLayerService } from './flat-layer';
import { FontService } from './font';
import { FormatBrushService } from './format-brush';
import { FrameService } from './frame';
import { History } from './history';
import { HotkeyService } from './hotkey';
import { LoadingService } from './loading';
import { MaskService } from './mask';
import { PPTService } from './ppt';
import { RegistrationService } from './registration';
import { ResultService } from './result';
import { ShineService } from './shine';
import { StageService } from './stage';
import { SvipWatermarkService } from './svip-watermark';
import { TextEngineService } from './text-engine';
import { TextHighlightService } from './text-highlight';
import { TextRenderService } from './text-render';
import { TransformService } from './transform';

/** 框架服务类 */
export class ServiceManager {
    private initEventName = 'service:init';
    private imageErrorEventName = 'service:image:error';
    /** 配置 */
    private config: IEditorConfig;
    /** 服务列表 */
    private services: IService[];
    /** 行为服务 */
    behavior: BehaviorService;
    /** 画布模糊服务 */
    blur: BlurService;
    /** 自定义数据服务 */
    customData: CustomDataService;
    /** 事件总线 */
    events: EventEmitter;
    /** 快捷键服务 */
    hotkey: HotkeyService;
    /** 历史记录服务 */
    history: History;
    /** 注册插件服务 */
    registration: RegistrationService;
    /** 结果合成服务 */
    result: ResultService;
    /** 无限画布服务 */
    edgelessCanvas: EdgelessCanvasService;
    /** PPT 操作服务 */
    edgelessPPT: PPTService;
    /** 图层数据服务 */
    flatLayer: FlatLayerService;
    /** 字体服务 */
    font: FontService;
    /** 文本引擎服务 */
    textEngine: TextEngineService;
    /**
     * 文字渲染服务。
     */
    textRender: TextRenderService;
    /** 当前编辑器的划重点素材目录 */
    textHighlight: TextHighlightService;
    /** 帧操作服务 */
    frame: FrameService;
    /** 坐标变换服务 */
    transform: TransformService;
    /** 编辑器服务 */
    editor: EditorService;
    /** 格式刷交互服务 */
    formatBrush: FormatBrushService;
    /** 图层加载服务 */
    loading: LoadingService;
    /** 全局遮罩服务 */
    mask: MaskService;
    /** 图层闪烁服务 */
    shine: ShineService;
    /** AI Agent 图层排版服务 */
    agentLayout: AgentLayoutService;
    /** SVIP 水印服务 */
    svipWatermark: SvipWatermarkService;
    /** 舞台业务服务 */
    stage: StageService;

    /**
     * 创建完整 framework service 集合。
     *
     * @param identity - 当前编辑器实例 ID。
     * @returns ServiceManager 实例。
     */
    constructor(readonly identity: string) {
        const eventGenerator = new EventGenerator('service', this, this);
        console.log('service attrs ==>', eventGenerator.attrs);

        this.config = {
            PUBLIC_URL: '',
        };

        this.behavior = new BehaviorService();
        this.customData = new CustomDataService(this);
        this.events = new EventEmitter();
        this.blur = new BlurService(this);
        this.hotkey = new HotkeyService(this);
        this.history = new History(this);
        this.registration = new RegistrationService(this);
        this.result = new ResultService(this);
        this.edgelessCanvas = new EdgelessCanvasService(this);
        this.edgelessPPT = new PPTService(this);
        this.flatLayer = new FlatLayerService(this);
        this.textEngine = new TextEngineService(this);
        this.textRender = new TextRenderService(this);
        this.textHighlight = new TextHighlightService();
        this.font = new FontService(this);
        this.frame = new FrameService(this);
        this.transform = new TransformService(this);
        this.editor = new EditorService(this);
        this.formatBrush = new FormatBrushService(this);
        this.loading = new LoadingService(this);
        this.mask = new MaskService(this);
        this.shine = new ShineService(this);
        this.agentLayout = new AgentLayoutService(this);
        this.svipWatermark = new SvipWatermarkService(this);
        this.stage = new StageService(this);

        this.services = [
            this.behavior,
            this.blur,
            this.customData,
            this.hotkey,
            this.history,
            this.registration,
            this.result,
            this.edgelessCanvas,
            this.flatLayer,
            this.textEngine,
            this.textRender,
            this.textHighlight,
            this.font,
            this.frame,
            this.edgelessPPT,
            this.transform,
            this.editor,
            this.formatBrush,
            this.loading,
            this.mask,
            this.shine,
            this.agentLayout,
            this.svipWatermark,
            this.stage,
        ];

        this.bindEvent();
    }

    /** 获取预览模式 ID */
    getPreviewModeId() {
        return `preview-${this.identity}`;
    }

    /** 初始化数据 */
    init() {
        this.services.forEach((service) => service.onInit());
        this.events.emit(this.initEventName);
    }

    /** 监听初始化事件 */
    onInit(cb: () => void) {
        return this.events.on(this.initEventName, cb);
    }

    /** 创建实例 */
    create() {
        this.services.forEach((service) => service.onCreate?.bind(service)());
    }

    /** 挂载dom */
    mount() {
        this.services.forEach((service) => service.onMount?.bind(service)());
    }

    /** 卸载dom */
    unmount() {
        this.services.forEach((service) => service.onUnmount?.bind(service)());
    }

    /** 绑定事件 */
    private bindEvent() {
        this.services.forEach((service) => service.onBindEvent?.bind(service)());
    }

    /** 获取配置 */
    getConfig() {
        return this.config;
    }

    /** 设置配置 */
    setConfig(config: IEditorConfig) {
        this.config = { ...this.config, ...config };
    }

    /** 捕获错误 */
    captureException(error: Error, options?: { extra?: Record<string, unknown>; level?: 'error' | 'log' }) {
        this.config.onCaptureException?.(error, options);
    }

    /** 捕获消息 */
    captureMessage(message: string, options?: { extra?: Record<string, unknown>; level?: 'error' | 'log' }) {
        this.config.onCaptureMessage?.(message, options);
    }

    /** 监听行为错误图片事件 */
    onImageError(fn: (error: { layerId: string; imageUrl: string }, url: string) => void, weight = 0) {
        return this.events.on(this.imageErrorEventName, fn, weight);
    }

    /**
     * 上报图片合成失败
     *
     * @param layerId - 图层 ID
     * @param imageUrl - 图片链接地址
     */
    reportImageError(error: { layerId: string; imageUrl: string }) {
        this.events.emit(this.imageErrorEventName, error);
    }
}
